import { omit, orderBy } from 'lodash';
import querystring from 'querystring';
import { createContext, ReactNode, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { useAppSelector } from 'store';
import { composableFiltersState } from 'store/composableFiltersSlice';
import {
  useGetJourneyProductListQuery,
  useGetProductJourneyQuery,
  useGetProductJourneySummaryQuery,
} from 'store/services/segmentsApi';
import {
  JourneyOrdersPath,
  JourneyProductList,
  JourneySummary,
} from 'store/services/types';
import { userDetailsState } from 'store/userDetailsSlice';
import { useParseURLQueryParams } from 'utils';

interface JourneyContextInterface {
  firstProduct: JourneyProductList | undefined;
  setFirstProduct: React.Dispatch<
    React.SetStateAction<JourneyProductList | undefined>
  >;
  secondProduct: JourneyOrdersPath | undefined;
  setSecondProduct: React.Dispatch<
    React.SetStateAction<JourneyOrdersPath | undefined>
  >;
  thirdProduct: JourneyOrdersPath | undefined;
  setThirdProduct: React.Dispatch<
    React.SetStateAction<JourneyOrdersPath | undefined>
  >;
  handleClearSelection: () => void;
  JourneySummaryStr: 'Loading...' | (string | JSX.Element)[];
  journeySummary: JourneySummary | undefined;
  productList: JourneyProductList[] | undefined;
  productListIsSuccess: boolean;
  journey: JourneyOrdersPath | undefined;
  journeyLoading: boolean;
  isJourneyError: boolean;
  secondOrders: JourneyOrdersPath[];
  thirdOrders: JourneyOrdersPath[];
}

export const JourneyContext = createContext({} as JourneyContextInterface);

export const JourneyContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [firstProduct, setFirstProduct] = useState<
    JourneyProductList | undefined
  >();
  const [secondProduct, setSecondProduct] = useState<
    JourneyOrdersPath | undefined
  >();
  const [thirdProduct, setThirdProduct] = useState<
    JourneyOrdersPath | undefined
  >();

  const history = useHistory();
  const { query, rawQueryParams } = useParseURLQueryParams();

  const queryParams = querystring.stringify(
    omit(
      {
        ...rawQueryParams,
      },
      ['first_product_id', 'second_product_id', 'third_product_id']
    )
  );

  const {
    output: { filtersApiPayload },
  } = useAppSelector(composableFiltersState);
  const { currentStore } = useAppSelector(userDetailsState);

  const locale = currentStore?.completeInfo?.primary_locale ?? 'en';

  const { data: journeySummary, isFetching: JourneySummaryLoading } =
    useGetProductJourneySummaryQuery(
      { serializedParams: queryParams, body: filtersApiPayload },
      {
        skip: !!!query.store_name || !!!query.start_date || !!!query.end_date,
      }
    );

  const { data: productList, isSuccess: productListIsSuccess } =
    useGetJourneyProductListQuery(
      { serializedParams: queryParams, body: filtersApiPayload },
      {
        skip: !!!query.store_name || !!!query.start_date || !!!query.end_date,
      }
    );

  const JourneySummaryStr = useMemo(() => {
    if (!!!journeySummary?.summary_text || JourneySummaryLoading)
      return 'Loading...';

    const splitStr = journeySummary?.summary_text.split(' ');

    return splitStr?.map((subStr, index) => {
      const numberPattern = /\d+/g;
      const isNumber = subStr.match(numberPattern);

      if (isNumber) {
        return (
          <span className="font-bold" key={index}>
            {' '}
            {Number(subStr).toLocaleString(locale)}{' '}
          </span>
        );
      }
      return ` ${subStr} `;
    });
  }, [JourneySummaryLoading, journeySummary?.summary_text, locale]);

  const handleClearSelection = () => {
    setFirstProduct(undefined);
    setSecondProduct(undefined);
    setThirdProduct(undefined);

    history.push({
      search: querystring.stringify(
        omit(
          {
            ...query,
          },
          ['first_product_id', 'second_product_id', 'third_product_id']
        )
      ),
    });
  };

  const productJourneyQuery = querystring.stringify(
    omit(
      {
        ...rawQueryParams,
      },
      ['second_product_id', 'third_product_id']
    )
  );

  const {
    data: journey,
    isFetching: journeyLoading,
    isError: isJourneyError,
  } = useGetProductJourneyQuery(
    { serializedParams: productJourneyQuery, body: filtersApiPayload },
    {
      skip:
        !!!query.store_name ||
        !!!query.first_product_id ||
        !!!query.start_date ||
        !!!query.end_date,
    }
  );

  const secondOrders = orderBy(
    journey?.next_orders,
    (item) => item.unique_customers,
    'desc'
  );

  const thirdOrdersRaw = secondOrders?.find(
    (item) => Number(item.id) === Number(query?.second_product_id)
  )?.next_orders;

  const thirdOrders = orderBy(
    thirdOrdersRaw,
    (item) => item.unique_customers,
    'desc'
  );

  return (
    <JourneyContext.Provider
      value={{
        firstProduct,
        setFirstProduct,
        secondProduct,
        setSecondProduct,
        thirdProduct,
        setThirdProduct,
        handleClearSelection,
        JourneySummaryStr,
        productList,
        productListIsSuccess,
        journey,
        journeyLoading,
        isJourneyError,
        secondOrders,
        thirdOrders,
        journeySummary,
      }}
    >
      {children}
    </JourneyContext.Provider>
  );
};
